import { Injectable } from '@angular/core';
import { params } from '@app/configurations';
import { ApiService } from '@app/core/api/api.service';
import { ApiCommonRoutes, ApiEndpoint } from '@app/core/models/api.models';
import { GlobalFilterType } from '@app/core/models/common.models';
import { SubFiltersService } from '@app/core/services/subfilters.service';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { GlobalFilterNotFound } from '@app/core/store/states/global-filters/global-filters-state.actions';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';
import { splitCompositeSubFilter } from '@app/core/utilities/refiners.utilities';
import { encodeSearchTerms } from '@app/core/utilities/url.utilities';
import { ForcedFilter } from '@app/shared/components';
import { defaultToastError } from '@app/shared/operators/default-toast-error.operator';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { ListItem } from '../models/selector.model';
import { BaseSelectorService } from './base-selector.service';
import { CampaignsDTO, getCampaignFilters, isMonitor } from './global-filters.utils';

@Injectable()
export class CampaignSelectorService extends BaseSelectorService {
  requiredParams = [
    params.GLOBAL_FILTER_CAMPAIGN_ID,
    params.GLOBAL_FILTER_CAMPAIGN_QUERY,
    params.GLOBAL_FILTER_DEST_CAMPAIGN_ID,
    params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY,
  ];
  includedData: any = [
    'accountName',
    'accountId',
    'campaignName',
    'campaignType',
    'adspertCampaignId',
    'accountPlatform',
    'campaignStatus',
    'performanceGroupName',
    'performanceGroupOptimizationType',
  ];
  defaultLabel = 'global-filter.campaign-selector.placeholder';
  defaultSubfiltersType = 'campaignsDefaultFilter';
  dto = CampaignsDTO;

  readonly srcSelectedItem$ = this.getSelectedItemStream(params.GLOBAL_FILTER_CAMPAIGN_ID);
  readonly destSelectedItem$ = this.getSelectedItemStream(params.GLOBAL_FILTER_DEST_CAMPAIGN_ID);

  readonly subfilterOptions = () => {
    const state = this.store.selectSnapshot(RouterNavState.queryParams);
    const filters: any = this.subFiltersService.getCampaignSubFilters(isMonitor() ? ['optiType'] : []);

    if (!state[params.GLOBAL_FILTER_ACCOUNT_QUERY]) return filters;
    else {
      const t = state[params.GLOBAL_FILTER_ACCOUNT_QUERY]
        .split(';')
        .filter((d) => d.toLowerCase().startsWith('platform'));
      if (!t.length) return filters;
      // there's no platform fitler selected return all options
      else {
        if (splitCompositeSubFilter(t[0])[1].toLowerCase() === 'all') return filters;
        else {
          return filters.map((item) => {
            if (item.key === 'type')
              item.options = item.options.filter((d) => splitCompositeSubFilter(t[0])[1].indexOf(d.platform) !== -1);
            return item;
          });
        }
      }
    }
  };

  getSelectedItem(id): Observable<ListItem> {
    if (this.cache.items?.[id]) return of(this.cache.items[id]);
    this.loader$.next(true);
    return this.store.select(CustomerState.customerId).pipe(
      switchMap((customerId) =>
        this.apiService
          .getObject({
            endpoint: ApiEndpoint.Customers,
            include: this.includedData,
            route: [customerId, ApiCommonRoutes.Campaigns, id],
          })
          .pipe(
            map(({ data }: any) => {
              this.loader$.next(false);
              this.cache.items[data.adspertCampaignId] = this.dto(data);
              return this.cache.items[data.adspertCampaignId];
            }),
            defaultToastError(
              'Failed to fetch selected Campaigns in the Global Filter. Please try with another selection.',
            ),
            catchError((err) => {
              this.store.dispatch(new GlobalFilterNotFound(GlobalFilterType.CAMPAIGN));
              this.loader$.next(false);
              return of({});
            }),
          ),
      ),
    );
  }

  getItems(
    { query, filters = [] },
    offset = 0,
    forDestination: boolean,
    forcedFilters?: ForcedFilter,
  ): Observable<{ items: ListItem[]; count: number }> {
    this.listLoader$.next(true);
    const state = this.store.selectSnapshot(RouterNavState.queryParams);
    const refiners: any = {
      sortValues: { campaignStatus: 1, campaignName: 2 },
      filterValues: {
        ...getCampaignFilters(
          filters.filter(([k]) => k.startsWith('filter_')),
          this.filterDefaults,
          state,
          forDestination,
        ),
        ...this.getForcedFilters(forcedFilters, forDestination),
      },
    };

    if (!!query) {
      refiners.searchFields =
        'accountId,accountName,accountTagsUser,amzPortfolioId,amzPortfolioName,campaignName,campaignId,performanceGroupName,campaignLabels';
      refiners.searchTerm = encodeSearchTerms(query);
    }

    return this.apiService
      .getList({
        endpoint: ApiEndpoint.Customers,
        route: [this.store.selectSnapshot(CustomerState.customerId), ApiCommonRoutes.Campaigns],
        include: this.includedData,
        ...refiners,
        searchValues: this.getSearchValues(filters),
        limit: this.limit,
        offset: offset,
      })
      .pipe(
        tap(() => this.listLoader$.next(false)),
        map(({ data, meta }) => {
          const items = data.map(this.dto);
          return { items, count: meta.pagination.total };
        }),
        defaultToastError('Failed to fetch Campaigns. Please try with another selection.'),
        catchError((err) => {
          this.listLoader$.next(false);
          return of({ items: [], count: 0 });
        }),
      );
  }

  constructor(
    translate: TranslateService,
    store: Store,
    apiService: ApiService,
    private subFiltersService: SubFiltersService,
  ) {
    super(translate, store, apiService);
  }
}
